import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  getOnboardingKeys,
  getOnboardingValues,
  saveOnboardingValue,
  uploadOnboardingValueFile,
  deleteOnboardingValue,
  deleteOnboardingValueFile,
  getOnboardingThemes,
  publishOnboarding,
  publishInitial,
  getOnboardingStatus,
} from "./service";
import BrandStudioSection from "./sections/BrandStudioSection";
import AccessControlSection from "./sections/AccessControlSection";
import ChecklistSection from "./sections/ChecklistSection";
import ThemeSection from "./sections/ThemeSection";
import StoreListingSection from "./sections/StoreListingSection";
import "./gizmott.css";
import { FaAccessibleIcon, FaThLarge } from "react-icons/fa";
import { ReactComponent as CopyIcon } from "../../assets/img/dashboard-svgs/copy.svg";
import { ReactComponent as CheckIcon } from "../../assets/img/dashboard-svgs/checkmark.svg";
import { ReactComponent as TrashIcon } from "../../assets/img/dashboard-svgs/ob-trash.svg";
import { ReactComponent as DownloadIcon } from "../../assets/img/dashboard-svgs/ob-download.svg";
import logo from "../../assets/img/illustrations/falcon.png";

const SAVE_DEBOUNCE_MS = 650;
const UPLOAD_TYPES = new Set(["file_upload", "image", "video"]);

const APP_STORE_PLATFORMS = [
  "apple_app_store",
  "google_play_console",
  "roku_developer",
  "amazon_fire_tv",
  "lg_tv",
  "samsung_tv",
  "android_tv",
  "apple_tv",
  "vizio_tv"
];

const isPaymentGatewaySlug = (slug) => {
  if (!slug) return false;
  const lower = slug.toLowerCase();
  if (APP_STORE_PLATFORMS.includes(lower)) return false;
  const appStoreKeywords = ["store", "play", "console", "developer", "tv"];
  return !appStoreKeywords.some(keyword => lower.includes(keyword));
};

const listFromResponse = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.data?.rows)) return response.data.rows;
  if (Array.isArray(response?.rows)) return response.rows;
  return [];
};

const normalizeKeysResponse = (response) => {
  const rows = listFromResponse(response);
  return rows.map((row) => ({
    ...row,
    key: row.key || row.question_key,
    key_order: row.key_order ?? row.question_order,
    first_time_required: row.first_time_required !== undefined ? row.first_time_required : (row.dashboard_access_required ? 1 : 0),
  }));
};

const normalizeValuesResponse = (response) => {
  const data = response?.data || response;
  let keys = [];
  let valuesMap = {};

  if (data?.keys && Array.isArray(data.keys)) {
    keys = normalizeKeysResponse(data.keys);
  }

  if (data?.values && typeof data.values === "object" && !Array.isArray(data.values)) {
    valuesMap = data.values;
  } else if (Array.isArray(data?.values)) {
    data.values.forEach((item) => {
      const k = item?.key || item?.question_key;
      if (k) valuesMap[k] = item;
    });
  }

  return { keys, valuesMap };
};

const extractFileItems = (entry) => {
  if (!entry) return { fileItems: [], firstUrl: "" };

  const raw = entry.value !== undefined ? entry.value : entry;
  if (Array.isArray(raw)) {
    const items = raw
      .filter((item) => item && typeof item === "object")
      .map((item) => ({
        value_id: item.value_id != null ? item.value_id : null,
        value: typeof item.value === "string" ? item.value : "",
        uploaded: item.uploaded != null ? item.uploaded : (typeof item.value === "string" ? 1 : 0),
      }))
      .filter((item) => item.value);
    return { fileItems: items, firstUrl: items[0]?.value || "" };
  }

  const url = typeof raw === "string" ? raw : (typeof entry.uploaded === "string" ? entry.uploaded : "");
  const singleItems = url ? [{
    value_id: entry.value_id != null ? entry.value_id : null,
    value: url,
    uploaded: entry.uploaded != null ? entry.uploaded : 1,
  }] : [];
  return { fileItems: singleItems, firstUrl: url };
};

const toUrlString = (v) => (typeof v === "string" ? v : "");

const safeOrder = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : Number.MAX_SAFE_INTEGER;
};

const parseOptions = (options) => {
  if (!options) return [];
  if (Array.isArray(options)) return options;
  if (typeof options === "string") {
    try {
      const parsed = JSON.parse(options);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return options.split(",").map((item) => item.trim()).filter(Boolean);
    }
  }
  if (typeof options === "object") {
    return Object.keys(options).map((key) => ({ value: key, label: options[key] }));
  }
  return [];
};

const isTruthyValue = (value) => {
  if (typeof value === "boolean") return true;
  if (value === 0) return true;
  const str = String(value || "").trim();
  if (!str) return false;
  if (/<[a-z][\s\S]*>/i.test(str)) {
    const text = str.replace(/<[^>]*>/g, "").replace(/&nbsp;/gi, "").trim();
    return text.length > 0;
  }
  return !!str;
};

const getFileUrlFromMeta = (meta, value) => {
  if (meta?.file_url) return meta.file_url;
  if (meta?.uploaded) return meta.uploaded;
  if (typeof value === "string" && value && /\.(png|jpg|jpeg|gif|svg|webp|mp4|mov|pdf|zip)/i.test(value)) return value;
  return "";
};

const getUploadedFiles = (value, meta) => {
  let files = [];

  if (Array.isArray(meta?.file_urls)) {
    meta.file_urls.forEach((url) => {
      if (typeof url === "string" && url) {
        files.push({ url, name: url.split("/").pop() || "File" });
      }
    });
  }

  if (typeof value === "string" && value.startsWith("[")) {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        parsed.forEach((item) => {
          const url = typeof item === "string" ? item : (typeof item?.url === "string" ? item.url : (typeof item?.file_url === "string" ? item.file_url : ""));
          if (url && !files.some((f) => f.url === url)) {
            files.push({ url, name: (typeof item === "object" && (item.file_name || item.name)) || url.split("/").pop() });
          }
        });
      }
    } catch (e) { /* not JSON */ }
  }

  if (typeof value === "string" && value && !value.startsWith("[") && /\.(png|jpg|jpeg|gif|svg|webp|mp4|mov|pdf|zip)/i.test(value)) {
    if (!files.some((f) => f.url === value)) {
      files.push({ url: value, name: value.split("/").pop() || "File" });
    }
  }

  return files;
};

const SECTION_COMPONENT_MAP = {
  brand: BrandStudioSection,
  brand_studio: BrandStudioSection,
  access: AccessControlSection,
  access_control: AccessControlSection,
  checklist: ChecklistSection,
  theme: ThemeSection,
  themes: ThemeSection,
  store: StoreListingSection,
  store_listing: StoreListingSection,
};

const getSectionIcon = (slug) => {
  const cleanSlug = String(slug || "").toLowerCase();
  if (cleanSlug === "brand" || cleanSlug === "brand_studio") {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 14.7255 3.09032 17.1962 4.85857 19C5.3211 19.4625 6 19 6 18.5V17C6 15.3431 7.34315 14 9 14H10C10.5523 14 11 13.5523 11 13C11 12.4477 10.5523 12 10 12C8.34315 12 7 10.6569 7 9C7 7.34315 8.34315 6 10 6C11.6569 6 13 7.34315 13 9C13 9.55228 13.4477 10 14 10H17C18.6569 10 20 11.3431 20 13C20 13.5523 19.5523 14 19 14H18.5C18 14 17.5 14.5 17.5 15C17.5 15.5 18 16 18 16.5C18 18.5 16.5 20 14.5 20H13C12.4477 20 12 20.4477 12 21V22Z" />
      </svg>
    );
  }
  if (cleanSlug === "access" || cleanSlug === "access_control") {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
      </svg>
    );
  }
  if (cleanSlug === "checklist") {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="9 11 12 14 22 4" />
        <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
      </svg>
    );
  }
  if (cleanSlug === "theme" || cleanSlug === "themes") {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
        <line x1="8" y1="21" x2="16" y2="21" />
        <line x1="12" y1="17" x2="12" y2="21" />
      </svg>
    );
  }
  if (cleanSlug === "store" || cleanSlug === "store_listing") {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
        <line x1="3" y1="6" x2="21" y2="6" />
        <path d="M16 10a4 4 0 0 1-8 0" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="4" width="16" height="16" rx="2" />
    </svg>
  );
};

const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`gob-toast ${type || "info"}`} style={{
      position: "fixed", bottom: 84, right: 24, zIndex: 9999,
      padding: "10px 18px", borderRadius: 8, fontSize: 13, fontWeight: 600,
      background: type === "error" ? "#dc2626" : "#10b981",
      color: "white", boxShadow: "0 4px 16px rgba(0,0,0,.15)",
      animation: "gob-fade-in .2s ease",
    }}>
      {message}
    </div>
  );
};

const DeleteConfirmModal = ({ onConfirm, onCancel, inProgress, title, message, confirmLabel, confirmStyle }) => (
  <div style={{
    position: "fixed", inset: 0, zIndex: 9998, display: "flex",
    alignItems: "center", justifyContent: "center",
    background: "rgba(0,0,0,.45)", backdropFilter: "blur(2px)",
  }}>
    <div style={{
      background: "var(--gob-bg-card, #fff)", borderRadius: 12,
      padding: "24px 28px", maxWidth: 400, width: "90%",
      boxShadow: "0 8px 32px rgba(0,0,0,.18)",
    }}>
      <h3 style={{ margin: "0 0 8px", fontSize: 16, fontWeight: 700 }}>{title || "Clear this value?"}</h3>
      <p style={{ margin: "0 0 20px", fontSize: 13, color: "var(--gob-ink-2, #666)", lineHeight: 1.6 }}>
        {message || "This removes the current value. You can add a new value anytime."}
      </p>
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
        <button
          className="gob-btn"
          onClick={onCancel}
          disabled={inProgress}
          style={{ padding: "7px 16px", borderRadius: 6, fontSize: 13, cursor: "pointer", background: "var(--gob-bg-soft, #f3f4f6)", border: "1px solid var(--gob-border, #e5e7eb)" }}
        >
          Cancel
        </button>
        <button
          className="gob-btn primary"
          onClick={onConfirm}
          disabled={inProgress}
          style={{ padding: "7px 16px", borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: "pointer", border: "none", ...(confirmStyle || { background: "#dc2626", color: "white" }) }}
        >
          {inProgress ? (confirmLabel ? `${confirmLabel.replace(/\.?$/, "…")}` : "Clearing…") : (confirmLabel || "Clear")}
        </button>
      </div>
    </div>
  </div>
);

const PublishConfirmModal = ({ onConfirm, onCancel, inProgress, isFirstTime }) => (
  <div style={{
    position: "fixed", inset: 0, zIndex: 9998, display: "flex",
    alignItems: "center", justifyContent: "center",
    background: "rgba(0,0,0,.5)", backdropFilter: "blur(3px)",
  }}>
    <div style={{
      background: "var(--gob-bg-card, #fff)", borderRadius: 14,
      padding: "28px 32px", maxWidth: 420, width: "90%",
      boxShadow: "0 12px 40px rgba(0,0,0,.22)",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
        <div style={{
          width: 40, height: 40, borderRadius: "50%",
          background: "linear-gradient(135deg,#6366f1,#8b5cf6)",
          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
        }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2">
            <line x1="22" y1="2" x2="11" y2="13" />
            <polygon points="22 2 15 22 11 13 2 9 22 2" />
          </svg>
        </div>
        <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>{isFirstTime ? "Submit for review?" : "Ready to publish?"}</h3>
      </div>
      <p style={{ margin: "0 0 22px", fontSize: 13, color: "var(--gob-ink-2, #555)", lineHeight: 1.6 }}>
        {isFirstTime
          ? "Your onboarding details will be submitted for review. Our team will verify and confirm your account before you get full dashboard access."
          : "This will push all your saved onboarding values to the production tables. Values that aren't wired up yet will be skipped and listed in the result."}
      </p>
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
        <button
          className="gob-btn"
          onClick={onCancel}
          disabled={inProgress}
          style={{ padding: "8px 18px", borderRadius: 7, fontSize: 13, cursor: "pointer", background: "var(--gob-bg-soft, #f3f4f6)", border: "1px solid var(--gob-border, #e5e7eb)" }}
        >
          Cancel
        </button>
        <button
          className="gob-btn primary"
          onClick={onConfirm}
          disabled={inProgress}
          style={{
            padding: "8px 20px", borderRadius: 7, fontSize: 13, fontWeight: 700,
            cursor: inProgress ? "default" : "pointer",
            background: "linear-gradient(135deg,#6366f1,#8b5cf6)",
            color: "white", border: "none",
            opacity: inProgress ? 0.7 : 1,
            display: "flex", alignItems: "center", gap: 6,
          }}
        >
          {inProgress ? (
            <>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" style={{ animation: "gob-spin 1s linear infinite" }}>
                <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
              </svg>
              {isFirstTime ? "Submitting…" : "Publishing…"}
            </>
          ) : (isFirstTime ? "Submit for Review" : "Publish")}
        </button>
      </div>
    </div>
  </div>
);

const stripHtmlTags = (value) => {
  if (typeof value !== "string") return value;
  return value
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
};

const PublisherOnboarding = () => {
  const [formSchema, setFormSchema] = useState([]);
  const [formValues, setFormValues] = useState({});
  const [fieldMeta, setFieldMeta] = useState({});
  const [themes, setThemes] = useState([]);
  const [collapsedSubsections, setCollapsedSubsections] = useState({});
  const [expandedHelp, setExpandedHelp] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [activeSection, setActiveSection] = useState("overview");
  const [focusedFieldKey, setFocusedFieldKey] = useState(null);
  const [platformContext, setPlatformContext] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteInProgress, setDeleteInProgress] = useState(false);
  const [toast, setToast] = useState(null);
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);
  const [publishInProgress, setPublishInProgress] = useState(false);
  const [publishResult, setPublishResult] = useState(null);
  const [copiedKey, setCopiedKey] = useState(null);
  const [sessionMismatch, setSessionMismatch] = useState(false);
  const loadedPubIdRef = useRef(localStorage.getItem("pub_id"));
  // const isSuperAdmin = useMemo(() => !!localStorage.getItem("superadmin"), []);
  const isSuperAdmin = false;
  const isFirstTime = useMemo(() => localStorage.getItem("onboarding_completed") === "false" && !isSuperAdmin, [isSuperAdmin]);

  const handleSessionMismatchRedirect = useCallback(() => {
    window.location.href = "/";
  }, []);

  useEffect(() => {
    const handleStorageChange = (e) => {
      if ((e.key === "pub_id" || e.key === "access-token") && loadedPubIdRef.current) {
        if (e.key === "pub_id" && e.newValue !== loadedPubIdRef.current) {
          setSessionMismatch(true);
          handleSessionMismatchRedirect();
        }
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [handleSessionMismatchRedirect]);

  const validateCurrentSession = useCallback(() => {
    const currentPubId = localStorage.getItem("pub_id");
    if (loadedPubIdRef.current && currentPubId !== loadedPubIdRef.current) {
      setSessionMismatch(true);
      handleSessionMismatchRedirect();
      return false;
    }
    return true;
  }, [handleSessionMismatchRedirect]);

  useEffect(() => {
    if (!isFirstTime) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await getOnboardingStatus();
        if (!cancelled && res?.success && res.data?.onboarding_completed) {
          window.location.href = "/";
        }
      } catch (e) {
      }
    })();
    return () => { cancelled = true; };
  }, [isFirstTime]);


  const handleCopy = (key, text) => {
    const cleanText = String(text || "").replace(/<[^>]*>/g, "");
    navigator.clipboard.writeText(cleanText);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 1500);
  };

  const saveTimersRef = useRef({});

  const showToast = useCallback((message, type = "success") => {
    setToast({ message, type, id: Date.now() });
  }, []);

  const loadSchemaAndValues = useCallback(async () => {
    let isMounted = true;

    try {
      setLoading(true);
      setLoadError("");

      const currentPubId = loadedPubIdRef.current || localStorage.getItem("pub_id");
      loadedPubIdRef.current = currentPubId;

      const [keysResponse, valuesResponse] = await Promise.all([
        getOnboardingKeys(),
        getOnboardingValues(),
      ]);
      let keysRows = normalizeKeysResponse(keysResponse);
      const normalized = normalizeValuesResponse(valuesResponse);
      if (normalized.keys.length > 0 && keysRows.length === 0) {
        keysRows = normalized.keys;
      }
      const valuesMap = normalized.valuesMap;

      if (!isMounted) return;

      keysRows.sort((a, b) => {
        const sCmp = safeOrder(a.section_order) - safeOrder(b.section_order);
        if (sCmp !== 0) return sCmp;
        const ssCmp = safeOrder(a.subsection_order) - safeOrder(b.subsection_order);
        if (ssCmp !== 0) return ssCmp;
        return safeOrder(a.key_order) - safeOrder(b.key_order);
      });

      const initialValues = {};
      const initialMeta = {};

      keysRows.forEach((row) => {
        const k = row.key;
        const meta = valuesMap[k] || {};
        const { fileItems, firstUrl } = extractFileItems(meta);
        const uploadedUrl = toUrlString(meta.uploaded) || firstUrl;

        if (fileItems.length > 1) {
          initialValues[k] = JSON.stringify(fileItems.map((f) => f.value));
        } else if (fileItems.length === 1) {
          initialValues[k] = fileItems[0].value;
        } else {
          initialValues[k] = typeof meta.value === "string" ? meta.value : "";
        }

        initialMeta[k] = {
          uploaded: uploadedUrl,
          delete_status: meta.delete_status || null,
          dirty: false,
          saving: false,
          error: "",
          lastSavedAt: null,
          file_url: uploadedUrl,
          file_items: fileItems,
          file_urls: fileItems.map((f) => f.value),
        };
      });

      setFormSchema(keysRows);
      setFormValues(initialValues);
      setFieldMeta(initialMeta);

      try {
        const themeResponse = await getOnboardingThemes();
        if (isMounted) setThemes(listFromResponse(themeResponse));
      } catch (e) { /* themes optional */ }
    } catch (error) {
      if (isMounted) setLoadError("Failed to load onboarding metadata.");
    } finally {
      if (isMounted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const handleFocusOrClick = (e) => {
      const row = e.target.closest("[data-key]");
      if (row) {
        const key = row.getAttribute("data-key");
        if (key) {
          setFocusedFieldKey(key);
        }
      }
    };

    document.addEventListener("focusin", handleFocusOrClick);
    document.addEventListener("click", handleFocusOrClick);
    return () => {
      document.removeEventListener("focusin", handleFocusOrClick);
      document.removeEventListener("click", handleFocusOrClick);
    };
  }, []);

  const prevActiveSectionRef = useRef(activeSection);
  useEffect(() => {
    if (prevActiveSectionRef.current !== activeSection) {
      prevActiveSectionRef.current = activeSection;
      if (activeSection === "overview") {
        setFocusedFieldKey(null);
        return;
      }

      const sectionQuestions = formSchema.filter(q => q.section_slug === activeSection);
      if (sectionQuestions.length > 0) {
        setFocusedFieldKey(sectionQuestions[0].key);
      } else {
        setFocusedFieldKey(null);
      }
    }
  }, [activeSection, formSchema]);

  const getNextQuestion = useCallback((currentKey) => {
    if (formSchema.length === 0) return null;

    let currentIndex = -1;
    if (currentKey) {
      currentIndex = formSchema.findIndex(q => q.key === currentKey);
    }

    if (currentIndex === -1) {
      if (activeSection === "overview") {
        return formSchema[0] || null;
      }
      const activeSectionQuestions = formSchema.filter(q => q.section_slug === activeSection);
      if (activeSectionQuestions.length > 0) {
        return activeSectionQuestions[0];
      }
      return formSchema[0] || null;
    }

    if (currentIndex + 1 < formSchema.length) {
      return formSchema[currentIndex + 1];
    }

    return null;
  }, [formSchema, activeSection]);

  const handleNextQuestion = useCallback(() => {
    const nextQ = getNextQuestion(focusedFieldKey);
    if (!nextQ) {
      setActiveSection("overview");
      return;
    }

    if (nextQ.section_slug !== activeSection) {
      setActiveSection(nextQ.section_slug);
    }

    setFocusedFieldKey(nextQ.key);

    const collapseKey = `${nextQ.section_slug}-${nextQ.subsection_slug}`;
    setCollapsedSubsections(prev => ({ ...prev, [collapseKey]: false }));

    setTimeout(() => {
      const el = document.getElementById(`q-row-${nextQ.key}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        const input = el.querySelector("input, textarea, select, button");
        if (input) {
          input.focus();
        }
      }
    }, 200);
  }, [getNextQuestion, focusedFieldKey, activeSection, showToast]);

  useEffect(() => {
    loadSchemaAndValues();
    return () => {
      Object.values(saveTimersRef.current).forEach((timer) => clearTimeout(timer));
      saveTimersRef.current = {};
    };
  }, [loadSchemaAndValues]);

  const refreshValues = useCallback(async () => {
    try {
      const valuesResponse = await getOnboardingValues();
      const { valuesMap } = normalizeValuesResponse(valuesResponse);
      setFormValues((prev) => {
        const next = { ...prev };
        Object.entries(valuesMap).forEach(([k, v]) => {
          next[k] = v.value ?? "";
        });
        return next;
      });
      setFieldMeta((prev) => {
        const next = { ...prev };
        Object.entries(valuesMap).forEach(([k, v]) => {
          const { fileItems } = extractFileItems(v);
          const uploadedUrl = toUrlString(v.uploaded) || fileItems[0]?.value || "";
          next[k] = {
            ...(next[k] || {}),
            uploaded: uploadedUrl,
            delete_status: v.delete_status || null,
            file_url: uploadedUrl,
            file_items: fileItems,
            file_urls: fileItems.map((f) => f.value),
          };
        });
        return next;
      });
    } catch (e) { /* silent */ }
  }, []);

  const grouped = useMemo(() => {
    const sectionMap = {};

    formSchema.forEach((row) => {
      const sectionSlug = row.section_slug || "unknown-section";
      const subsectionSlug = row.subsection_slug || "unknown-subsection";

      if (!sectionMap[sectionSlug]) {
        sectionMap[sectionSlug] = {
          sectionSlug,
          sectionName: row.section_name || sectionSlug,
          sectionOrder: safeOrder(row.section_order),
          subsections: {},
        };
      }

      if (!sectionMap[sectionSlug].subsections[subsectionSlug]) {
        sectionMap[sectionSlug].subsections[subsectionSlug] = {
          subsectionSlug,
          subsectionName: row.subsection_name || subsectionSlug,
          subsectionOrder: safeOrder(row.subsection_order),
          questions: [],
        };
      }

      sectionMap[sectionSlug].subsections[subsectionSlug].questions.push(row);
    });

    return Object.values(sectionMap)
      .sort((a, b) => a.sectionOrder - b.sectionOrder)
      .map((section) => ({
        ...section,
        subsections: Object.values(section.subsections)
          .sort((a, b) => a.subsectionOrder - b.subsectionOrder)
          .map((subsection) => ({
            ...subsection,
            questions: subsection.questions.sort((a, b) => safeOrder(a.key_order) - safeOrder(b.key_order)),
          })),
      }));
  }, [formSchema]);

  const isPaymentProviderKey = useCallback((key) => {
    const question = formSchema.find(q => q.key === key);
    if (!question) return false;
    const isAccessSection = question.section_slug === "access" || question.section_slug === "access_control";
    return isAccessSection && isPaymentGatewaySlug(question.subsection_slug);
  }, [formSchema]);

  const computeCompletedRequired = useCallback((requiredKeys) => {
    const paymentKeys = requiredKeys.filter(q => isPaymentProviderKey(q.key));
    const nonPaymentKeys = requiredKeys.filter(q => !isPaymentProviderKey(q.key));

    let completedCount = nonPaymentKeys.filter(q => isTruthyValue(formValues[q.key])).length;

    if (paymentKeys.length > 0) {
      const keysByProvider = {};
      paymentKeys.forEach(q => {
        const provider = q.subsection_slug;
        if (!keysByProvider[provider]) {
          keysByProvider[provider] = [];
        }
        keysByProvider[provider].push(q);
      });

      const anyProviderDone = Object.values(keysByProvider).some(keys =>
        keys.length > 0 && keys.every(q => isTruthyValue(formValues[q.key]))
      );

      if (anyProviderDone) {
        completedCount += 1;
      }
    }

    return completedCount;
  }, [formValues, isPaymentProviderKey]);

  const adjustedRequiredTotal = useCallback((requiredKeys) => {
    const hasPayment = requiredKeys.some(q => isPaymentProviderKey(q.key));
    const nonPayment = requiredKeys.filter(q => !isPaymentProviderKey(q.key));
    return nonPayment.length + (hasPayment ? 1 : 0);
  }, [isPaymentProviderKey]);

  const requiredStats = useMemo(() => {
    const requiredKeys = formSchema.filter((row) => {
      if (isFirstTime) {
        return row.first_time_required === 1 || row.first_time_required === true;
      }
      if (row.required_when_key) {
        return formValues[row.required_when_key] === row.required_when_value;
      }
      return !!row.is_required;
    });
    const total = adjustedRequiredTotal(requiredKeys);
    const completed = computeCompletedRequired(requiredKeys);
    return {
      total,
      completed,
      percent: total ? Math.round((completed / total) * 100) : 100,
    };
  }, [formSchema, formValues, isFirstTime, computeCompletedRequired, adjustedRequiredTotal]);

  const canPublish = useMemo(() => {
    if (formSchema.length === 0) return false;
    return requiredStats.completed >= requiredStats.total;
  }, [formSchema, requiredStats]);

  const allFirstTimeRequiredFilled = useMemo(() => {
    const firstTimeKeys = formSchema.filter(q => q.first_time_required === 1 || q.first_time_required === true);
    if (firstTimeKeys.length === 0) return true;
    return firstTimeKeys.every(q => isTruthyValue(formValues[q.key]));
  }, [formSchema, formValues]);

  const isPublishDisabled = useMemo(() => {
    if (publishInProgress || sessionMismatch) return true;
    if (formSchema.length === 0) return true;
    if (isFirstTime) {
      return !canPublish;
    } else {
      return !allFirstTimeRequiredFilled;
    }
  }, [publishInProgress, sessionMismatch, formSchema.length, isFirstTime, canPublish, allFirstTimeRequiredFilled]);

  const sectionSummary = useCallback((section) => {
    const all = section.subsections.flatMap((sub) => sub.questions);
    const required = all.filter((q) => {
      if (isFirstTime) {
        return q.first_time_required === 1 || q.first_time_required === true;
      }
      if (q.required_when_key) {
        return formValues[q.required_when_key] === q.required_when_value;
      }
      return !!q.is_required;
    });
    const completedRequired = computeCompletedRequired(required);
    const total = adjustedRequiredTotal(required);
    const answeredAny = all.filter((q) => isTruthyValue(formValues[q.key])).length;

    const cls = total
      ? (completedRequired === total ? "complete" : completedRequired > 0 ? "partial" : "empty")
      : (answeredAny > 0 ? "partial" : "empty");

    const pct = total ? Math.round((completedRequired / total) * 100) : 0;

    return {
      required, completedRequired, answeredAny, cls, pct,
      label: total ? `${completedRequired}/${total}` : "",
    };
  }, [formValues, isFirstTime, computeCompletedRequired, adjustedRequiredTotal]);

  const saveNow = useCallback(async (field, value, platform = null) => {
    const key = field.key;

    if (!validateCurrentSession()) {
      setFieldMeta((prev) => ({
        ...prev,
        [key]: { ...prev[key], saving: false, error: "Publisher session changed in another tab. Reload page." },
      }));
      return;
    }

    const charLimits = field.char_limits || {};

    if (Object.keys(charLimits).length > 0) {
      const currentLength = String(value || "").length;
      const limits = Object.entries(charLimits).map(([p, l]) => [p, Number(l)]);
      limits.sort((a, b) => a[1] - b[1]);
      const smallestLimit = limits[0][1];
      const smallestPlatform = limits[0][0];

      if (currentLength > smallestLimit) {
        setFieldMeta((prev) => ({
          ...prev,
          [key]: { ...prev[key], error: `Cannot save - exceeds character limit for ${smallestPlatform} (${currentLength}/${smallestLimit})` },
        }));
        return;
      }
    }

    setFieldMeta((prev) => ({
      ...prev,
      [key]: { ...prev[key], saving: true, error: "" },
    }));

    try {
      let effectivePlatform = platform || platformContext[key] || null;
      const isStoreField = /^store_/.test(key);
      if (isStoreField && !effectivePlatform) {
        const limits = Object.entries(charLimits).map(([p, l]) => [p, Number(l)]);
        if (limits.length > 0) {
          limits.sort((a, b) => a[1] - b[1]);
          effectivePlatform = limits[0][0];
        }
      }

      const payload = { value };
      if (effectivePlatform) payload.platform = effectivePlatform;

      await saveOnboardingValue(key, payload);

      setFieldMeta((prev) => ({
        ...prev,
        [key]: { ...prev[key], saving: false, dirty: false, lastSavedAt: Date.now(), error: "" },
      }));

      window.setTimeout(() => {
        setFieldMeta((prev) => ({
          ...prev,
          [key]: { ...prev[key], lastSavedAt: null },
        }));
      }, 1400);
    } catch (error) {
      let errorMsg = "Save failed. Retry on next change.";
      if (error.response?.status === 409) {
        errorMsg = error.response?.data?.message || "Session mismatched. Redirecting...";
        setSessionMismatch(true);
        handleSessionMismatchRedirect();
      } else if (error.response?.status === 400) {
        errorMsg = error.response?.data?.message || error.response?.data?.validation_message || errorMsg;
      }
      setFieldMeta((prev) => ({
        ...prev,
        [key]: { ...prev[key], saving: false, error: errorMsg },
      }));
    }
  }, [platformContext, validateCurrentSession]);

  const scheduleSave = useCallback((field, value, immediate = false, platform = null) => {
    const key = field.key;
    if (saveTimersRef.current[key]) clearTimeout(saveTimersRef.current[key]);

    if (immediate) {
      saveNow(field, value, platform);
      return;
    }

    saveTimersRef.current[key] = setTimeout(() => {
      saveNow(field, value, platform);
      delete saveTimersRef.current[key];
    }, SAVE_DEBOUNCE_MS);
  }, [saveNow]);

  const updateField = useCallback((field, value, immediate = false, platform = null, skipSave = false) => {
    if (!validateCurrentSession()) return;
    const key = field.key;
    const currentValue = formValues[key];

    if (currentValue !== value) {
      setFormValues((prev) => {
        const next = { ...prev, [key]: value };
        if (key === "checklist_about_us_text") {
          next["store_description_short"] = value;
        } else if (key === "checklist_about_us") {
          next["store_description_long"] = stripHtmlTags(value);
        }
        return next;
      });

      setFieldMeta((prev) => {
        const next = {
          ...prev,
          [key]: { ...prev[key], dirty: true, error: "" }
        };
        if (key === "checklist_about_us_text") {
          next["store_description_short"] = { ...(prev["store_description_short"] || {}), dirty: true, error: "" };
        } else if (key === "checklist_about_us") {
          next["store_description_long"] = { ...(prev["store_description_long"] || {}), dirty: true, error: "" };
        }
        return next;
      });
    }

    if (platform) {
      setPlatformContext((prev) => ({ ...prev, [key]: platform }));
    }

    if (!skipSave) {
      scheduleSave(field, value, immediate, platform);
    }

    if (key === "checklist_about_us_text") {
      const targetField = formSchema.find((q) => q.key === "store_description_short");
      if (targetField && !skipSave) {
        scheduleSave(targetField, value, immediate, platform);
      }
    } else if (key === "checklist_about_us") {
      const targetField = formSchema.find((q) => q.key === "store_description_long");
      if (targetField && !skipSave) {
        scheduleSave(targetField, stripHtmlTags(value), immediate, platform);
      }
    }
  }, [formValues, scheduleSave, formSchema, validateCurrentSession]);

  const handleUpload = useCallback(async (field, fileOrFiles) => {
    if (!validateCurrentSession()) {
      showToast("Publisher session changed in another tab. Reload page.", "error");
      return;
    }
    const files = Array.isArray(fileOrFiles) ? fileOrFiles.filter(Boolean) : [fileOrFiles].filter(Boolean);
    if (!files.length) return;
    const key = field.key;
    const isMulti = Array.isArray(fileOrFiles) && fileOrFiles.length > 0;

    const prevMeta = fieldMeta[key] || {};
    const prevValue = formValues[key] || "";

    setFieldMeta((prev) => ({
      ...prev,
      [key]: { ...prev[key], saving: true, error: "" },
    }));

    try {
      const response = await uploadOnboardingValueFile(key, files.length === 1 ? files[0] : files);
      const data = response?.data || response;
      const fileUrl = toUrlString(data?.file_url || data?.uploaded);
      const newFileUrls = Array.isArray(data?.file_urls) ? data.file_urls.filter((u) => typeof u === "string" && u) : [];

      if (isMulti) {
        let existingItems = [];
        if (Array.isArray(prevMeta.file_items) && prevMeta.file_items.length > 0) {
          existingItems = prevMeta.file_items
            .filter((item) => item && typeof item.value === "string" && item.value)
            .map((item) => ({ value_id: item.value_id, value: item.value, uploaded: item.uploaded }));
        } else {
          if (typeof prevValue === "string" && prevValue.startsWith("[")) {
            try {
              const parsed = JSON.parse(prevValue);
              if (Array.isArray(parsed)) {
                parsed.forEach((item) => {
                  const url = typeof item === "string" ? item : (typeof item?.url === "string" ? item.url : "");
                  if (url) existingItems.push({ value_id: null, value: url, uploaded: 1 });
                });
              }
            } catch (e) { /* not JSON */ }
          } else if (typeof prevValue === "string" && prevValue && /\.(png|jpg|jpeg|gif|svg|webp|mp4|mov|pdf|zip)/i.test(prevValue)) {
            existingItems = [{ value_id: null, value: prevValue, uploaded: 1 }];
          }
        }

        let newItems = [];
        if (Array.isArray(data?.value)) {
          data.value.forEach((item) => {
            if (item && typeof item === "object" && typeof item.value === "string" && item.value) {
              newItems.push({
                value_id: item.value_id != null ? item.value_id : null,
                value: item.value,
                uploaded: item.uploaded != null ? item.uploaded : 1,
              });
            }
          });
        } else if (newFileUrls.length > 0) {
          newItems = newFileUrls.map((u) => ({ value_id: null, value: u, uploaded: 1 }));
        } else if (fileUrl) {
          newItems = [{ value_id: data?.value_id != null ? data.value_id : null, value: fileUrl, uploaded: 1 }];
        }

        const mergedItems = [...existingItems];
        newItems.forEach((item) => {
          if (item.value_id != null) {
            if (!mergedItems.some((m) => m.value_id === item.value_id)) mergedItems.push(item);
          } else if (item.value) {
            if (!mergedItems.some((m) => m.value === item.value)) mergedItems.push(item);
          }
        });

        const mergedUrls = mergedItems.map((item) => item.value);
        const finalValue = mergedUrls.length > 1
          ? JSON.stringify(mergedUrls)
          : (mergedUrls[0] || fileUrl || prevValue || "");

        setFormValues((prev) => ({ ...prev, [key]: finalValue }));

        setFieldMeta((prev) => ({
          ...prev,
          [key]: {
            ...prev[key],
            saving: false,
            uploaded: fileUrl || mergedUrls[0] || "",
            file_url: fileUrl || mergedUrls[0] || "",
            file_urls: mergedUrls,
            file_items: mergedItems,
            dirty: false,
            lastSavedAt: Date.now(),
            error: "",
          },
        }));
      } else {
        setFormValues((prev) => ({
          ...prev,
          [key]: data?.value ?? fileUrl ?? prev[key],
        }));
        setFieldMeta((prev) => ({
          ...prev,
          [key]: {
            ...prev[key],
            saving: false,
            uploaded: fileUrl,
            file_url: fileUrl,
            file_urls: newFileUrls.length ? newFileUrls : prev[key]?.file_urls,
            dirty: false,
            lastSavedAt: Date.now(),
            error: "",
          },
        }));
      }

      window.setTimeout(() => {
        setFieldMeta((prev) => ({
          ...prev,
          [key]: { ...prev[key], lastSavedAt: null },
        }));
      }, 1400);
    } catch (error) {
      if (error?.response?.status === 409) {
        setSessionMismatch(true);
        handleSessionMismatchRedirect();
      }
      setFieldMeta((prev) => ({
        ...prev,
        [key]: { ...prev[key], saving: false, error: "Upload failed. Try again." },
      }));
    }
  }, [refreshValues, formValues, fieldMeta, validateCurrentSession, showToast]);

  const handleRemoveFile = useCallback(async (key, fileUrl, valueId) => {
    if (!validateCurrentSession()) {
      showToast("Publisher session changed in another tab. Reload page.", "error");
      return;
    }
    const meta = fieldMeta[key] || {};
    const currentValue = formValues[key] || "";

    let currentItems = [];

    if (Array.isArray(meta.file_items) && meta.file_items.length > 0) {
      currentItems = meta.file_items
        .filter((item) => item && typeof item.value === "string" && item.value)
        .map((item) => ({ value_id: item.value_id, value: item.value, uploaded: item.uploaded }));
    } else {
      const urls = [];
      if (Array.isArray(meta.file_urls)) {
        meta.file_urls.forEach((u) => { if (typeof u === "string" && u) urls.push(u); });
      }
      if (typeof currentValue === "string" && currentValue.startsWith("[")) {
        try {
          const parsed = JSON.parse(currentValue);
          if (Array.isArray(parsed)) {
            parsed.forEach((item) => {
              const url = typeof item === "string" ? item : (typeof item?.url === "string" ? item.url : "");
              if (url && !urls.includes(url)) urls.push(url);
            });
          }
        } catch (e) { /* not JSON */ }
      }
      if (typeof currentValue === "string" && currentValue && !currentValue.startsWith("[") && /\.(png|jpg|jpeg|gif|svg|webp|mp4|mov|pdf|zip)/i.test(currentValue)) {
        if (!urls.includes(currentValue)) urls.push(currentValue);
      }
      currentItems = urls.map((u) => ({ value_id: null, value: u, uploaded: 1 }));
    }

    let remainingItems;
    if (valueId != null) {
      remainingItems = currentItems.filter((item) => item.value_id !== valueId);
    } else {
      remainingItems = currentItems.filter((item) => item.value !== fileUrl);
    }

    const remainingUrls = remainingItems.map((item) => item.value);
    const newValue = remainingUrls.length > 1
      ? JSON.stringify(remainingUrls)
      : (remainingUrls[0] || "");

    const prevValue = currentValue;
    const prevMeta = meta;

    setFormValues((prev) => ({ ...prev, [key]: newValue }));
    setFieldMeta((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        saving: true,
        uploaded: remainingUrls[0] || "",
        file_url: remainingUrls[0] || "",
        file_urls: remainingUrls,
        file_items: remainingItems,
        error: "",
      },
    }));

    try {
      if (remainingItems.length === 0) {
        await deleteOnboardingValue(key);
        setFormValues((prev) => ({ ...prev, [key]: "" }));
        setFieldMeta((prev) => ({
          ...prev,
          [key]: { ...prev[key], saving: false, uploaded: "", file_url: "", file_urls: [], file_items: [], dirty: false, lastSavedAt: Date.now() },
        }));
      } else {
        if (valueId != null) {
          await deleteOnboardingValueFile(key, valueId);
        } else {
          await saveOnboardingValue(key, { value: newValue });
        }
        setFieldMeta((prev) => ({
          ...prev,
          [key]: { ...prev[key], saving: false, dirty: false, lastSavedAt: Date.now(), error: "" },
        }));
      }
      showToast("File removed", "success");

      window.setTimeout(() => {
        setFieldMeta((prev) => ({
          ...prev,
          [key]: { ...prev[key], lastSavedAt: null },
        }));
      }, 1400);
    } catch (error) {
      // Rollback
      setFormValues((prev) => ({ ...prev, [key]: prevValue }));
      setFieldMeta((prev) => ({
        ...prev,
        [key]: { ...prevMeta, saving: false, error: "Failed to remove file" },
      }));
      showToast("Failed to remove file", "error");
    }
  }, [formValues, fieldMeta, showToast, validateCurrentSession]);

  const handleDelete = useCallback(async (key) => {
    if (!validateCurrentSession()) {
      showToast("Publisher session changed in another tab. Reload page.", "error");
      return;
    }
    setDeleteInProgress(true);
    const prevValue = formValues[key];
    const prevMeta = fieldMeta[key];

    setFormValues((prev) => ({ ...prev, [key]: "" }));
    setFieldMeta((prev) => ({
      ...prev,
      [key]: { ...prev[key], saving: true, error: "", uploaded: "", file_url: "", file_urls: [], file_items: [] },
    }));

    try {
      await deleteOnboardingValue(key);

      setFieldMeta((prev) => ({
        ...prev,
        [key]: { ...prev[key], saving: false, dirty: false, lastSavedAt: Date.now() },
      }));
      showToast("Value cleared", "success");

      window.setTimeout(() => {
        setFieldMeta((prev) => ({
          ...prev,
          [key]: { ...prev[key], lastSavedAt: null },
        }));
      }, 1400);
    } catch (error) {
      setFormValues((prev) => ({ ...prev, [key]: prevValue }));
      setFieldMeta((prev) => ({
        ...prev,
        [key]: { ...prevMeta, saving: false, error: "Failed to clear value" },
      }));
      showToast("Failed to clear value", "error");
    } finally {
      setDeleteInProgress(false);
      setDeleteTarget(null);
    }
  }, [formValues, fieldMeta, showToast, validateCurrentSession]);

  const handlePublish = useCallback(async () => {
    if (!validateCurrentSession()) {
      showToast("Publisher session changed in another tab. Reload page.", "error");
      return;
    }
    setPublishInProgress(true);
    try {
      const response = isFirstTime ? await publishInitial() : await publishOnboarding();
      setShowPublishConfirm(false);
      if (isFirstTime) {
        showToast(response?.message || "Submission received! Our team will review and confirm your account.", "success");
      } else {
        showToast(response?.message || "Published successfully!", "success");
      }
    } catch (error) {
      setShowPublishConfirm(false);
      const msg = error?.response?.data?.message || "Publish failed. Please try again.";
      showToast(msg, "error");
    } finally {
      setPublishInProgress(false);
    }
  }, [showToast, isFirstTime, validateCurrentSession]);

  const clearField = useCallback(async (key) => {
    if (!validateCurrentSession()) return;
    const prevValue = formValues[key];
    const prevMeta = fieldMeta[key];

    setFormValues((prev) => ({ ...prev, [key]: "" }));
    setFieldMeta((prev) => ({
      ...prev,
      [key]: { ...prev[key], saving: true, error: "", uploaded: "", file_url: "", file_urls: [], file_items: [] },
    }));

    try {
      await deleteOnboardingValue(key);
      setFieldMeta((prev) => ({
        ...prev,
        [key]: { ...prev[key], saving: false, dirty: false, lastSavedAt: Date.now() },
      }));

      window.setTimeout(() => {
        setFieldMeta((prev) => ({
          ...prev,
          [key]: { ...prev[key], lastSavedAt: null },
        }));
      }, 1400);
    } catch (error) {
      setFormValues((prev) => ({ ...prev, [key]: prevValue }));
      setFieldMeta((prev) => ({
        ...prev,
        [key]: { ...prevMeta, saving: false, error: "Failed to clear" },
      }));
    }
  }, [formValues, fieldMeta, validateCurrentSession]);

  const renderFieldStatus = (key) => {
    const meta = fieldMeta[key] || {};
    if (meta.saving) return <span className="q-help">Saving...</span>;
    if (meta.error) return <span className="gob-field-error">{meta.error}</span>;
    if (meta.lastSavedAt) return <span className="q-help">Saved</span>;
    return null;
  };

  const renderInput = (field, sectionSlug, isSuperAdmin) => {
    const key = field.key;
    const value = formValues[key] ?? "";
    const options = parseOptions(field.options);
    const meta = fieldMeta[key] || {};
    const isFontField =
      field.input_type === "single_select" &&
      (/font/i.test(field.key || "") || /font/i.test(field.prompt || ""));
    const isThemeField =
      field.input_type === "single_select" &&
      (/theme/i.test(sectionSlug || "") || /theme/i.test(field.section_name || "") || /theme/i.test(field.subsection_name || ""));

    if (field.input_type === "textarea") {
      return (
        <textarea
          className="gob-input"
          rows={4}
          value={value}
          onChange={(e) => updateField(field, e.target.value)}
          onBlur={(e) => updateField(field, e.target.value, true)}
          disabled={isSuperAdmin}
        />
      );
    }

    if (field.input_type === "yes_no") {
      return (
        <div className="q-options">
          <button type="button" className={`q-option ${value === "yes" ? "selected yes" : ""}`} disabled={isSuperAdmin} onClick={() => !isSuperAdmin && updateField(field, "yes", true)}>Yes</button>
          <button type="button" className={`q-option ${value === "no" ? "selected no" : ""}`} disabled={isSuperAdmin} onClick={() => !isSuperAdmin && updateField(field, "no", true)}>No</button>
        </div>
      );
    }

    if (field.input_type === "single_select") {
      if (isThemeField && themes.length > 0) {
        return (
          <div className="theme-grid" style={{ marginTop: 10 }}>
            {themes.map((theme) => {
              const themeValue = theme.theme_slug || theme.value || theme.id;
              const themeName = theme.display_name || theme.name || themeValue;
              const selected = String(value || "") === String(themeValue || "");

              return (
                <button type="button" key={themeValue || themeName} className={`theme-card ${selected ? "selected" : ""}`} disabled={isSuperAdmin} onClick={() => !isSuperAdmin && updateField(field, themeValue, true)}>
                  <div className="theme-thumb">
                    {theme.thumbnail_url && <img src={theme.thumbnail_url} alt={themeName} style={{ width: "100%", height: "100%", objectFit: "cover" }} />}
                  </div>
                  <div className="theme-info">
                    <div className="theme-name">{themeName}</div>
                    <div className="theme-meta">
                      <div className="theme-best-for">Dynamic Theme</div>
                      {theme.preview_url && (
                        <a className="theme-preview-btn" href={theme.preview_url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>Preview</a>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        );
      }

      return (
        <select className="gob-select" value={value} style={isFontField && value ? { fontFamily: String(value) } : undefined} onChange={(e) => updateField(field, e.target.value)} onBlur={(e) => updateField(field, e.target.value, true)} disabled={isSuperAdmin}>
          <option value="">Select an option</option>
          {options.map((option) => {
            const optVal = typeof option === "object" ? option.value ?? option.label : option;
            const optLbl = typeof option === "object" ? option.label ?? option.value : option;
            return (
              <option key={String(optVal)} value={optVal} style={isFontField && optVal ? { fontFamily: String(optVal) } : undefined}>{optLbl}</option>
            );
          })}
        </select>
      );
    }

    if (UPLOAD_TYPES.has(field.input_type)) {
      const uploadedFiles = getUploadedFiles(value, meta);
      const fileUrl = getFileUrlFromMeta(meta, value);
      return (
        <div style={{ display: "grid", gap: 8 }}>
          {!isSuperAdmin ? (
            <input type="file" className="gob-input" onChange={(e) => handleUpload(field, e.target.files?.[0])} />
          ) : uploadedFiles.length === 0 && !fileUrl ? (
            <span style={{ fontSize: 12, color: "var(--gob-ink-3)" }}>No files uploaded</span>
          ) : null}
          {(fileUrl || uploadedFiles.length > 0) && (
            <div className="q-help">
              {meta.uploaded ? "Uploaded" : "File available"}
              {fileUrl && <>{" "}- <a href={fileUrl} target="_blank" rel="noopener noreferrer">Open file</a></>}
            </div>
          )}
          {uploadedFiles.length > 1 && (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {uploadedFiles.map((f, idx) => (
                <a key={f.url} href={f.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12 }}>{`File ${idx + 1}`}</a>
              ))}
            </div>
          )}
        </div>
      );
    }

    return (
      <input className="gob-input" type="text" value={value} onChange={(e) => updateField(field, e.target.value)} onBlur={(e) => updateField(field, e.target.value, true)} disabled={isSuperAdmin} />
    );
  };

  const renderOverview = () => {
    const completeSections = grouped.filter((s) => sectionSummary(s).cls === "complete").length;
    const partialSections = grouped.filter((s) => sectionSummary(s).cls === "partial").length;

    return (
      <div>
        <div className="project-hero">
          <div className="hero-eyebrow">Publisher Onboarding</div>
          <h1 className="hero-title">{localStorage.getItem("user_name")}</h1>
          <p className="hero-sub">Setup items to complete before our team begins building. Work through them in any order — we'll let you know when everything's ready.</p>
          <div className="hero-meta">
            <div className="hero-meta-item">
              <span className="hero-meta-label">Required completion</span>
              <span className="hero-meta-value">{requiredStats.completed}/{requiredStats.total}</span>
            </div>
            <div className="hero-meta-item">
              <span className="hero-meta-label">Sections</span>
              <span className="hero-meta-value">{grouped.length}</span>
            </div>
          </div>
        </div>

        <div className="progress-overview">
          <div className="overview-head">
            <div className="overview-title">Setup progress</div>
            <div className="overview-pct">{requiredStats.percent}%</div>
          </div>
          <div className="overview-bar">
            <div className="overview-bar-fill" style={{ width: `${requiredStats.percent}%` }} />
          </div>
          <div className="overview-legend">
            <span className="legend-item"><span className="legend-dot complete" />{completeSections} complete</span>
            <span className="legend-item"><span className="legend-dot partial" />{partialSections} in progress</span>
            <span className="legend-item"><span className="legend-dot empty" />{grouped.length - completeSections - partialSections} not started</span>
          </div>
        </div>

        <div className="items-grid">
          {grouped.map((section, index) => {
            const summary = sectionSummary(section);
            return (
              <div key={section.sectionSlug} className="item-card" onClick={() => setActiveSection(section.sectionSlug)}>
                <div className="item-card-num">{String(index + 1).padStart(2, "0")}</div>
                <div className={`item-card-icon ${section.sectionSlug}`}>
                  {getSectionIcon(section.sectionSlug)}
                </div>
                <div>
                  <div className="item-card-name">{section.sectionName}</div>
                  <div className="item-card-desc">{section.subsections.length} subsections</div>
                </div>
                <div className="item-card-progress">
                  <div className={`item-card-progress-fill ${summary.cls}`} style={{ width: `${summary.pct}%` }} />
                </div>
                <div className="item-card-footer">
                  <span className={`item-card-status ${summary.cls}`}><span className="dot" />{summary.label}</span>
                  <span className="item-card-arrow">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const savingByKey = useMemo(() => {
    const map = {};
    Object.keys(fieldMeta).forEach((k) => { if (fieldMeta[k].saving) map[k] = true; });
    return map;
  }, [fieldMeta]);

  const savedByKey = useMemo(() => {
    const map = {};
    Object.keys(fieldMeta).forEach((k) => { if (fieldMeta[k].lastSavedAt) map[k] = true; });
    return map;
  }, [fieldMeta]);

  const errorByKey = useMemo(() => {
    const map = {};
    Object.keys(fieldMeta).forEach((k) => { if (fieldMeta[k].error) map[k] = fieldMeta[k].error; });
    return map;
  }, [fieldMeta]);


  const renderSection = (section) => {
    if (!section) return null;

    const SpecializedComponent = SECTION_COMPONENT_MAP[section.sectionSlug];
    if (SpecializedComponent) {
      return (
        <SpecializedComponent
          section={section}
          formValues={formValues}
          fieldMeta={fieldMeta}
          updateField={updateField}
          handleUpload={handleUpload}
          handleRemoveFile={handleRemoveFile}
          requestDelete={(key) => setDeleteTarget(key)}
          clearField={clearField}
          savingByKey={savingByKey}
          savedByKey={savedByKey}
          errorByKey={errorByKey}
          themes={themes}
          platformContext={platformContext}
          setPlatformContext={setPlatformContext}
          isSuperAdmin={isSuperAdmin}
          isFirstTime={isFirstTime}
          focusedFieldKey={focusedFieldKey}
        />
      );
    }

    return (
      <div>
        <div className="gob-page-head">
          <div className="gob-page-eyebrow">Section</div>
          <div className="gob-page-title">{section.sectionName}</div>
          <div className="gob-page-sub">Subsections and fields are rendered from metadata.</div>
        </div>

        {section.subsections.map((subsection, index) => {
          const collapseKey = `${section.sectionSlug}-${subsection.subsectionSlug}`;
          const isCollapsed = !!collapsedSubsections[collapseKey];
          return (
            <div key={collapseKey} className={`section-card ${isCollapsed ? "" : "open"}`}>
              <div className="section-head" onClick={() => setCollapsedSubsections((prev) => ({ ...prev, [collapseKey]: !prev[collapseKey] }))}>
                <div className={`section-icon ${["violet", "cyan", "amber", "green", "rose"][index % 5]}`}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 7h16M4 12h16M4 17h10" /></svg>
                </div>
                <div className="section-title-block">
                  <div className="section-title">{subsection.subsectionName}</div>
                  <div className="section-meta">{subsection.questions.length} fields</div>
                </div>
                <svg className="section-chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9" /></svg>
              </div>

              <div className="section-body">
                {subsection.questions.map((field) => {
                  const key = field.key;
                  const value = formValues[key] ?? "";
                  const answered = isTruthyValue(value);
                  const useStackedInput = field.input_type === "textarea" || UPLOAD_TYPES.has(field.input_type);
                  const rowClass = `q-row ${answered ? "answered" : ""}${useStackedInput ? " q-input-row" : ""}`;
                  const meta = fieldMeta[key] || {};
                  const hasValue = isTruthyValue(value) || !!meta.uploaded;

                  return (
                    <div key={key} id={`q-row-${key}`} data-key={key} className={rowClass}>
                      <div>
                        <div className="q-text">
                          {field.prompt}
                          {(isFirstTime ? (field.first_time_required === 1 || field.first_time_required === true) : field.is_required) ? (
                            <span className="gob-req-tag">Required</span>
                          ) : (
                            <span className="gob-opt-tag">Optional</span>
                          )}
                        </div>
                        {field.tooltip && <div className="q-help">{field.tooltip}</div>}
                        {renderFieldStatus(key)}

                        {field.help_detail_html && (
                          <div style={{ marginTop: 8 }}>
                            <button type="button" className="theme-preview-btn" onClick={() => setExpandedHelp((prev) => ({ ...prev, [key]: !prev[key] }))}>
                              {expandedHelp[key] ? "Hide help" : "Show help"}
                            </button>
                            {expandedHelp[key] && (
                              <div className="q-help" style={{ marginTop: 8 }} dangerouslySetInnerHTML={{ __html: field.help_detail_html }} />
                            )}
                          </div>
                        )}
                      </div>

                      <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 6 }}>
                        {renderInput(field, section.sectionSlug, isSuperAdmin)}

                        {hasValue && (
                          <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8, justifyContent: "flex-end" }}>
                            {(field.input_type === "textarea" || (!["textarea", "single_select", "yes_no"].includes(field.input_type) && !UPLOAD_TYPES.has(field.input_type))) && (
                              <button
                                type="button"
                                onClick={() => handleCopy(key, value)}
                                className="gob-btn-icon"
                                title="Copy text"
                                style={{ background: "none", border: "none", cursor: "pointer", padding: 4, display: "inline-flex", color: copiedKey === key ? "var(--gob-success, #10b981)" : "var(--gob-ink-3, #9ca3af)", transition: "color 0.15s" }}
                              >
                                {copiedKey === key ? <CheckIcon style={{ width: 12, height: 12 }} /> : <CopyIcon style={{ width: 12, height: 12 }} />}
                              </button>
                            )}

                            {UPLOAD_TYPES.has(field.input_type) && getFileUrlFromMeta(meta, value) && (
                              <a
                                href={getFileUrlFromMeta(meta, value)}
                                download
                                target="_blank"
                                rel="noopener noreferrer"
                                className="gob-btn-icon"
                                title="Download file"
                                style={{ background: "none", border: "none", cursor: "pointer", padding: 4, display: "inline-flex", color: "var(--gob-ink-3, #9ca3af)", transition: "color 0.15s" }}
                              >
                                <DownloadIcon style={{ width: 12, height: 12 }} />
                              </a>
                            )}

                            {field.input_type !== "yes_no" && !isSuperAdmin && (
                              <button
                                type="button"
                                disabled={meta.saving}
                                onClick={() => setDeleteTarget(key)}
                                className="gob-btn-icon"
                                title="Clear value"
                                style={{ background: "none", border: "none", cursor: "pointer", padding: 4, display: "inline-flex", color: "#ef4444", opacity: meta.saving ? 0.5 : 1, transition: "opacity 0.15s" }}
                              >
                                <TrashIcon style={{ width: 12, height: 12 }} />
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="gob-root standalone">
        <div className="gob-empty-state">Loading onboarding flow...</div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="gob-root standalone">
        <div className="gob-empty-state" style={{ color: "#dc2626" }}>{loadError}</div>
      </div>
    );
  }

  const currentSection = grouped.find((s) => s.sectionSlug === activeSection);

  return (
    <div className="gob-root standalone">
      <div className="gob-app">
        <aside className="gob-sidebar">
          <div className="gob-logo">
            <img
              src={logo}
              alt="Gizmott Logo"
              style={{
                maxHeight: "40px",
                maxWidth: "120px",
                objectFit: "contain",
              }}
            />
          </div>

          <div className="gob-project-card">
            <div className="gob-project-label">{localStorage.getItem("user_name")}</div>
            <div className="gob-project-name">Onboarding</div>
          </div>

          <div className="gob-nav-section-label">Setup</div>

          <button className={`gob-nav-item ${activeSection === "overview" ? "active" : ""}`} onClick={() => setActiveSection("overview")}>
            <span className="gob-nav-num overview"><FaThLarge /></span>
            Overview
          </button>

          {grouped.map((section, index) => {
            const summary = sectionSummary(section);
            return (
              <button
                key={section.sectionSlug}
                className={`gob-nav-item ${activeSection === section.sectionSlug ? "active" : ""}`}
                onClick={() => setActiveSection(section.sectionSlug)}
              >
                <span className="gob-nav-num">{String(index + 1)}</span>
                {section.sectionName}
                <span className={`gob-nav-status ${summary.cls}`}>{summary.label}</span>
              </button>
            );
          })}

          <div style={{ padding: "10px 0 0", borderTop: "1px solid var(--gob-line, #ececf1)", marginTop: 8 }}>
            <button
              className="gob-nav-item"
              onClick={() => setShowPublishConfirm(true)}
              disabled={isPublishDisabled}
              style={{
                width: "100%",
                background: isPublishDisabled ? "var(--gob-bg-soft, #f3f4f6)" : "var(--gob-ink, #0E0B1F)",
                color: isPublishDisabled ? "var(--gob-ink-3, #9ca3af)" : "white",
                borderRadius: 8,
                fontWeight: 700,
                opacity: publishInProgress ? 0.6 : 1,
                cursor: isPublishDisabled ? "not-allowed" : "pointer",
                border: isPublishDisabled ? "1px solid var(--gob-border, #e5e7eb)" : "none",
              }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={isPublishDisabled ? "var(--gob-ink-3, #9ca3af)" : "white"} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                <line x1="22" y1="2" x2="11" y2="13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
              {publishInProgress ? (isFirstTime ? "Submitting…" : "Publishing…") : (isFirstTime ? "Submit for Review" : "Publish")}
            </button>
          </div>
        </aside>

        <main className="gob-main">
          {activeSection === "overview" ? renderOverview() : renderSection(currentSection)}
        </main>
      </div>

      {deleteTarget && (
        <DeleteConfirmModal
          inProgress={deleteInProgress}
          onConfirm={() => handleDelete(deleteTarget)}
          onCancel={() => { if (!deleteInProgress) setDeleteTarget(null); }}
        />
      )}

      {showPublishConfirm && (
        <PublishConfirmModal
          inProgress={publishInProgress}
          onConfirm={handlePublish}
          onCancel={() => { if (!publishInProgress) setShowPublishConfirm(false); }}
          isFirstTime={isFirstTime}
        />
      )}

      {activeSection !== "overview" && (
        <button
          className="gob-next-q-btn"
          onClick={handleNextQuestion}
        >
          {getNextQuestion(focusedFieldKey) ? (
            <>
              <span>
                {getNextQuestion(focusedFieldKey).section_slug !== activeSection
                  ? `${getNextQuestion(focusedFieldKey).section_name || getNextQuestion(focusedFieldKey).section_slug}`
                  : "Next"}
              </span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </>
          ) : (
            <>
              <span>Back to Overview</span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                <polyline points="9 22 9 12 15 12 15 22" />
              </svg>
            </>
          )}
        </button>
      )}

      {/* Session mismatch message modal - commented out per requirement to logout session instead
      {sessionMismatch && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 99999,
          background: "rgba(15, 23, 42, 0.75)", backdropFilter: "blur(4px)",
          display: "flex", alignItems: "center", justifyContent: "center", padding: 20
        }}>
          <div style={{
            background: "var(--gob-bg-card, #fff)", borderRadius: 14,
            padding: "32px 36px", maxWidth: 460, width: "100%",
            boxShadow: "0 20px 50px rgba(0,0,0,0.3)", textAlign: "center"
          }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>⚠️</div>
            <h3 style={{ margin: "0 0 10px", fontSize: 18, fontWeight: 700, color: "#dc2626" }}>
              Publisher Session Changed
            </h3>
            <p style={{ fontSize: 14, color: "var(--gob-ink-2, #4b5563)", lineHeight: 1.6, margin: "0 0 24px" }}>
              Your publisher account session was changed in another tab or window. To prevent modifying the wrong account data, saving has been disabled for this tab.
            </p>
            <button
              className="gob-btn primary"
              onClick={() => window.location.reload()}
              style={{
                padding: "10px 24px", borderRadius: 8, fontSize: 14, fontWeight: 600,
                background: "#6366f1", color: "white", border: "none", cursor: "pointer"
              }}
            >
              Reload Page
            </button>
          </div>
        </div>
      )}
      */}

      {toast && <Toast key={toast.id} message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
};

export default PublisherOnboarding;
